import React, { useState, useEffect, useMemo } from "react"
import { Col, Row, Select, Space, Typography } from "antd"
import { TbPigMoney } from "react-icons/tb"
// **Import doc and getDoc for fetching single document**
import { collection, query, getDocs, where, doc, getDoc } from "firebase/firestore"
import { db } from "../../../firebase/config"
import PageHeader from "../../../components/Elements/PageHeader"
import Overview from "../../Admin/Sales/SalesReport/Overview"
import TopClients from "../../Admin/Sales/SalesReport/TopClients"
import Chart from "../../Admin/Sales/SalesReport/Chart"
import LastMonths from "../../Admin/Sales/SalesReport/LastMonths"
import { useAuthContext } from "../../../hooks/useAuthContext"

const { Text } = Typography
const { Option } = Select

export default function SalesReportForSales() {
  const { user } = useAuthContext() // Get authenticated user context

  // Selected filters
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear())
  // **Initialize selectedMonth as string for consistency with Option keys**
  const [selectedMonth, setSelectedMonth] = useState(String(new Date().getMonth() + 1))

  // All invoices fetched once (remains the same)
  const [allInvoices, setAllInvoices] = useState([])
  const [allInvoicesLoading, setAllInvoicesLoading] = useState(false)

  // Warehouse orders (remains the same)
  const [warehouseOrders, setWarehouseOrders] = useState([])
  const [warehouseOrdersLoading, setWarehouseOrdersLoading] = useState(false)

  // --- NEW STATE for Sales Report Data ---
  const [salesReportData, setSalesReportData] = useState(null)
  const [salesReportLoading, setSalesReportLoading] = useState(false)
  // --- END NEW STATE ---

  // Handlers (remain the same)
  const handleSelectYear = (value) => setSelectedYear(value)
  const handleSelectMonth = (value) => setSelectedMonth(value)

  // Generate year options (remains the same)
  const currentYear = new Date().getFullYear()
  const years = []
  for (let y = 2018; y <= currentYear; y++) {
    years.push(y)
  }

  // Month options (remains the same)
  const months = [
    { key: "1", label: "January" },
    { key: "2", label: "February" },
    { key: "3", label: "March" },
    { key: "4", label: "April" },
    { key: "5", label: "May" },
    { key: "6", label: "June" },
    { key: "7", label: "July" },
    { key: "8", label: "August" },
    { key: "9", label: "September" },
    { key: "10", label: "October" },
    { key: "11", label: "November" },
    { key: "12", label: "December" },
    // Optional: Add whole year if needed, but current logic focuses on monthly reports
    // { key: "wholeYear", label: "Whole Year" },
  ]

  // Fetch all invoices once (remains the same)
  useEffect(() => {
    async function fetchAllInvoices() {
      setAllInvoicesLoading(true)
      try {
        // Optimization: Only fetch invoices relevant to the logged-in user if possible
        // If chartInvoices already filters by user.uid, maybe fetch only those initially?
        // Current implementation fetches ALL invoices.
        const q = query(collection(db, "invoices")) // Consider adding where("userId", "==", user.uid) if feasible
        const querySnapshot = await getDocs(q)
        const invoicesData = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }))
        // Filter invoices for the current user *here* if not done in the query
        const userInvoices = invoicesData.filter((inv) => inv.userId === user?.uid)
        setAllInvoices(userInvoices) // Store only user's invoices if sufficient
        // If you still need *all* invoices for other admin views, keep the original logic
        // setAllInvoices(invoicesData);
      } catch (error) {
        console.error("Error fetching invoices: ", error)
        setAllInvoices([]) // Set empty on error
      }
      setAllInvoicesLoading(false)
    }
    if (user?.uid) {
      // Only fetch if user is available
      fetchAllInvoices()
    } else {
      setAllInvoices([]) // Clear invoices if no user
      setAllInvoicesLoading(false)
    }
  }, [user]) // Depend on user

  // Get warehouse orders (remains the same)
  useEffect(() => {
    async function fetchWarehouseOrders() {
      // Only run if user is available
      if (!user || !user.uid) {
        setWarehouseOrders([])
        setWarehouseOrdersLoading(false)
        return
      }
      setWarehouseOrdersLoading(true)
      try {
        // Filter by user.id directly in the query
        const constraints = [where("orderStatus", "==", 6), where("uid", "==", user.uid)]
        const q = query(collection(db, "orders"), ...constraints)
        const querySnapshot = await getDocs(q)
        const ordersData = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }))
        console.log("warehouseOrder, :", ordersData)
        // The extra filter for "E1X..." seems redundant if already filtering by user.uid, unless that user IS the one to exclude
        setWarehouseOrders(ordersData)
      } catch (error) {
        console.error("Error fetching warehouse orders: ", error)
        setWarehouseOrders([]) // Set empty on error
      }
      setWarehouseOrdersLoading(false)
    }
    fetchWarehouseOrders()
  }, [user]) // Depend on user

  // --- useEffect to fetch Sales Report Document ---
  useEffect(() => {
    const fetchSalesReport = async () => {
      if (!user || !user.uid || selectedMonth === "wholeYear") {
        setSalesReportData(null)
        setSalesReportLoading(false)
        // *** ADD LOG ***
        console.log("PARENT useEffect: Skipping fetch (no user/month or wholeYear)")
        return
      }

      setSalesReportLoading(true)
      // *** FIX TYPO and ensure correct ID generation ***
      const currentMonthString = String(selectedMonth).padStart(2, "0") // Ensure month is two digits (e.g., '04')
      const reportId = `${user.uid}_${selectedYear}_${currentMonthString}` // Use padded month
      const reportRef = doc(db, "salesReports", reportId)

      try {
        console.log(`PARENT useEffect: Fetching sales report ID: ${reportId}`)
        const docSnap = await getDoc(reportRef)

        if (docSnap.exists()) {
          const data = docSnap.data()
          // *** ADD DETAILED LOG ***
          console.log("PARENT useEffect: Sales report FOUND. Data:", JSON.stringify(data)) // Log the raw data structure
          setSalesReportData(data)
        } else {
          console.warn(`PARENT useEffect: Sales report document NOT FOUND: ${reportId}`)
          setSalesReportData(null)
        }
      } catch (error) {
        console.error("PARENT useEffect: Error fetching sales report:", error)
        setSalesReportData(null)
      } finally {
        setSalesReportLoading(false)
      }
    }

    fetchSalesReport()
    // *** Ensure dependency array includes user.uid for reactivity ***
  }, [user?.uid, selectedYear, selectedMonth, user])
  // --- END NEW useEffect ---

  // Filters for components (remain the same, but now operate on potentially user-filtered allInvoices)
  // Note: These filter based on invoiceDate, ensure your invoices have this field correctly
  const yearInvoices = useMemo(
    () =>
      allInvoices.filter((invoice) => {
        if (!invoice.invoiceDate?.seconds) return false
        const invoiceDate = new Date(invoice.invoiceDate.seconds * 1000)
        return invoiceDate.getFullYear() === selectedYear
      }),
    [allInvoices, selectedYear]
  )

  const monthInvoices = useMemo(() => {
    if (selectedMonth === "wholeYear") return [] // Use yearInvoices if month is wholeYear? Or empty?
    return allInvoices.filter((invoice) => {
      if (!invoice.invoiceDate?.seconds) return false
      const invoiceDate = new Date(invoice.invoiceDate.seconds * 1000)
      return (
        invoiceDate.getFullYear() === selectedYear &&
        invoiceDate.getMonth() === parseInt(selectedMonth, 10) - 1
      )
    })
  }, [allInvoices, selectedYear, selectedMonth])

  const prevYearInvoices = useMemo(
    () =>
      allInvoices.filter((invoice) => {
        if (!invoice.invoiceDate?.seconds) return false
        const invoiceDate = new Date(invoice.invoiceDate.seconds * 1000)
        return invoiceDate.getFullYear() === selectedYear - 1
      }),
    [allInvoices, selectedYear]
  )

  const prevMonthInvoices = useMemo(() => {
    if (selectedMonth === "wholeYear") return []
    return allInvoices.filter((invoice) => {
      if (!invoice.invoiceDate?.seconds) return false
      const invoiceDate = new Date(invoice.invoiceDate.seconds * 1000)
      return (
        invoiceDate.getFullYear() === selectedYear - 1 &&
        invoiceDate.getMonth() === parseInt(selectedMonth, 10) - 1
      )
    })
  }, [allInvoices, selectedYear, selectedMonth])

  // chartInvoices already filters by user if allInvoices is pre-filtered
  const chartInvoices = allInvoices // Simpler if allInvoices only contains user's invoices

  return (
    <div style={{ padding: 15 }}>
      <Row gutter={[15, 15]}>
        <Col span={24}>
          <PageHeader text="Sales Overview" icon={<TbPigMoney />} />
        </Col>
        <Col span={24}>
          <Space size="large">
            <Space>
              <Text>Year:</Text>
              <Select style={{ width: 250 }} value={selectedYear} onChange={handleSelectYear}>
                {years.map((year) => (
                  <Option key={year} value={year}>
                    {year}
                  </Option>
                ))}
              </Select>
            </Space>
            <Space>
              <Text>Month:</Text>
              <Select style={{ width: 250 }} value={selectedMonth} onChange={handleSelectMonth}>
                {months.map((month) => (
                  <Option key={month.key} value={month.key}>
                    {month.label}
                  </Option>
                ))}
              </Select>
            </Space>
          </Space>
        </Col>
        <Col span={24}>
          {/* Overview gets the parent's year/month filters AND the new props */}
          <Overview
            // Pass invoice data as before (used for Month/Year totals blocks)
            yearInvoices={yearInvoices}
            monthInvoices={monthInvoices}
            prevYearInvoices={prevYearInvoices}
            prevMonthInvoices={prevMonthInvoices}
            loading={allInvoicesLoading} // Loading state for invoice totals blocks
            selectedYear={selectedYear}
            selectedMonth={selectedMonth}
            months={months}
            warehouseOrders={warehouseOrders}
            warehouseOrdersLoading={warehouseOrdersLoading}
            forSales={true}
            salesReportData={salesReportData}
            salesReportLoading={salesReportLoading}
            // --- END PASS NEW PROPS ---
          />
        </Col>
        <Col span={24}>
          {/* Chart gets the chartInvoices (filtered only by user) */}
          <Chart invoices={chartInvoices} loading={allInvoicesLoading} />
        </Col>

        <Col span={8}>
          <TopClients
            invoices={chartInvoices}
            loading={allInvoicesLoading}
            selectedYear={selectedYear}
            selectedMonth={selectedMonth}
          />
        </Col>
        <Col span={8}>
          <TopClients
            invoices={chartInvoices}
            loading={allInvoicesLoading}
            selectedYear={selectedYear}
            mode="yearly"
          />
        </Col>
        {/* Check span value, 8+8+6 = 22, maybe adjust last one? */}
        <Col span={8}>
          {/* Ensure selectedManager prop is correct if needed */}
          <LastMonths invoices={chartInvoices} selectedManager={user?.uid} />
        </Col>
      </Row>
    </div>
  )
}
